import { useState } from "react";
import { IconeExterno, IconeTrocar } from "../icones";
import { LinkInterno } from "../navegacao";
import { Selo, type Tom } from "./Basicos";
import { Botao } from "./Botao";
import { Flutuante } from "./Flutuante";
import { CopiarValor } from "./Pagina";

/** O plano que a area mostra agora, ja pronto pra desenhar (o app traduz a matricula; a regra fica no backend). */
export type PlanoNaCasca = {
  nome: string;
  /** Uma linha curta embaixo do nome: a etapa por extenso, o status, "2 planos". */
  apoio?: string;
  /** Barra de segmentos (as etapas da familia) e qual e a atual, comecando em 0. */
  etapas?: { lista: string[]; atual: number };
  /** O numero da conta que a pessoa digita na plataforma, com copiar (o nome do campo ficou da Prop). */
  subconta?: string | null;
  /** O rotulo desse numero: "Subconta" (padrao, Prop) ou "Conta" (Global, onde a conta e um numero so). */
  rotuloDaConta?: string;
  /** O proximo passo em uma linha. */
  passo?: string | null;
  /** Selo quando ha o que fazer (ou o estado pede destaque: bloqueado, encerrado). */
  selo?: { rotulo: string; tom: Tom } | null;
  /** normal: tonal; bloqueado: cor de erro com a acao de pagar; encerrado: neutro, so consulta. */
  situacao?: "normal" | "bloqueado" | "encerrado";
  /** A acao do cartao (bloqueado: "Pagar mensalidade"). */
  acao?: { rotulo: string; href: string } | null;
};

/** Uma opcao do seletor, com a linha de apoio (etapa ou o que esta pendente). `anterior`: plano encerrado. */
export type OpcaoDePlano = { id: string; nome: string; apoio: string; atual: boolean; anterior?: boolean };

/**
 * Cartao do plano atual na casca da area (spec 038, secao 2): nome, etapa com a barra, a conta com copiar (subconta na
 * Prop, conta na Global, pelo `rotuloDaConta`), o proximo
 * passo numa linha e um selo quando ha acao. Com 2 ou mais planos o topo do cartao e o seletor (abre pelo `Flutuante`:
 * folha inferior no celular); com 1, nao tem cara de botao. Na gaveta do desktop fica embaixo do logo; no trilho e no
 * celular vira `faixa`, no topo do conteudo.
 * Estados: carregando (esqueleto), vazio ("Conheça os planos"), erro (tentar de novo), bloqueado, encerrado.
 */
export function CartaoDoPlanoAtual({ estado, plano, opcoes, verTodos, aoEscolher, linkPlanos, comprarHref, aoTentarDeNovo, variante = "gaveta" }: {
  estado: "carregando" | "vazio" | "erro" | "pronto";
  plano?: PlanoNaCasca;
  /** Dois ou mais: o cartao vira o seletor. */
  opcoes?: OpcaoDePlano[];
  /** Mostra "Ver todos os planos" no seletor; `true` quando e a escolha atual. */
  verTodos?: boolean;
  aoEscolher?: (id: string | "todos") => void;
  /** Sem plano: pra onde vai o "Conheça os planos" (o site da marca). */
  linkPlanos?: string;
  /** Sem plano, com os planos a venda dentro da area (15/10): o endereco interno; tem preferencia sobre o site. */
  comprarHref?: string;
  aoTentarDeNovo?: () => void;
  variante?: "gaveta" | "faixa";
}) {
  const [aberto, setAberto] = useState(false);
  const classe = `mtc-plano-casca mtc-plano-casca--${variante}`;
  if (estado === "carregando") {
    return (
      <div className={`${classe} mtc-plano-casca--carregando`} role="status" aria-label="Carregando seu plano">
        <span className="mtc-esqueleto mtc-esqueleto--titulo" />
        <span className="mtc-esqueleto mtc-esqueleto--linha" />
        <span className="mtc-esqueleto mtc-esqueleto--barra" />
      </div>
    );
  }
  if (estado === "erro") {
    return (
      <div className={`${classe} mtc-plano-casca--encerrado`}>
        <span className="mtc-plano-casca__passo">Não deu pra carregar seus planos agora.</span>
        {aoTentarDeNovo && <div><Botao variante="fantasma" tamanho="pequeno" onClick={aoTentarDeNovo}>Tentar de novo</Botao></div>}
      </div>
    );
  }
  if (estado === "vazio" || !plano) {
    return (
      <div className={`${classe} mtc-plano-casca--encerrado`}>
        <strong className="mtc-plano-casca__nome mtc-plano-casca__nome--livre">Você ainda não tem um plano</strong>
        <span className="mtc-plano-casca__apoio">Quando comprar, ele aparece aqui.</span>
        {comprarHref ? <LinkInterno className="mtc-botao mtc-botao--secundario mtc-botao--pequeno mtc-plano-casca__botao" href={comprarHref}>Ver planos à venda</LinkInterno> : linkPlanos && <a className="mtc-botao mtc-botao--secundario mtc-botao--pequeno mtc-plano-casca__botao" href={linkPlanos} target="_blank" rel="noreferrer">Conheça os planos<IconeExterno tamanho={16} /></a>}
      </div>
    );
  }
  const seletor = Boolean(opcoes && opcoes.length > 1 && aoEscolher);
  const situacao = plano.situacao ?? "normal";
  const topo = (
    <>
      <span className="mtc-plano-casca__textos">
        <span className="mtc-plano-casca__nome">{plano.nome}</span>
        {plano.apoio && <span className="mtc-plano-casca__apoio">{plano.apoio}</span>}
      </span>
      {seletor && <IconeTrocar tamanho={20} className="mtc-plano-casca__trocar" />}
    </>
  );
  const escolher = (id: string) => { setAberto(false); aoEscolher?.(id); };
  const emAndamento = (opcoes ?? []).filter((o) => !o.anterior);
  const anteriores = (opcoes ?? []).filter((o) => o.anterior);
  const item = (o: OpcaoDePlano) => (
    <button key={o.id} type="button" role="menuitem" className="mtc-plano-casca__opcao" aria-current={!verTodos && o.atual} onClick={() => escolher(o.id)}>
      <strong>{o.nome}</strong>
      <span className="mtc-plano-casca__apoio">{o.apoio}</span>
    </button>
  );
  return (
    <section className={`${classe} mtc-plano-casca--${situacao}`} aria-label="Plano atual">
      <div className="mtc-plano-casca__cabeca">
        {seletor ? (
          <button type="button" className="mtc-plano-casca__topo mtc-plano-casca__topo--botao" aria-haspopup="dialog" aria-expanded={aberto} onClick={() => setAberto(!aberto)}>{topo}</button>
        ) : (
          <div className="mtc-plano-casca__topo">{topo}</div>
        )}
        {seletor && (
          <Flutuante aberto={aberto} aoFechar={() => setAberto(false)} titulo="Escolha o plano" descricao="As telas de plano e do financeiro mostram o escolhido." classe="mtc-plano-casca__lista">
            <div role="menu" aria-label="Seus planos" className="mtc-plano-casca__opcoes">
              {emAndamento.map(item)}
              {anteriores.length > 0 && (
                <div role="group" aria-label="Planos anteriores" className="mtc-plano-casca__opcoes">
                  <span className="mtc-plano-casca__grupo">Planos anteriores</span>
                  {anteriores.map(item)}
                </div>
              )}
              <button type="button" role="menuitem" className="mtc-plano-casca__opcao" aria-current={Boolean(verTodos)} onClick={() => escolher("todos")}>
                <strong>Ver todos os planos</strong>
                <span className="mtc-plano-casca__apoio">Mostra os {opcoes!.length} juntos nas telas de plano</span>
              </button>
            </div>
          </Flutuante>
        )}
      </div>
      {plano.selo && <Selo tom={plano.selo.tom}>{plano.selo.rotulo}</Selo>}
      {plano.etapas && plano.etapas.lista.length > 0 && (
        <ol className="mtc-plano-casca__etapas" aria-label={`Etapa ${plano.etapas.atual + 1} de ${plano.etapas.lista.length}: ${plano.etapas.lista[plano.etapas.atual] ?? ""}`}>
          {plano.etapas.lista.map((e, i) => <li key={e} title={e} data-estado={i < plano.etapas!.atual ? "feita" : i === plano.etapas!.atual ? "atual" : "futura"} />)}
        </ol>
      )}
      {plano.subconta && (
        <div className="mtc-plano-casca__subconta">
          <span className="mtc-plano-casca__rotulo">{plano.rotuloDaConta ?? "Subconta"}</span>
          <span className="mtc-mono">{plano.subconta}</span>
          <CopiarValor valor={plano.subconta} rotulo={`Copiar ${(plano.rotuloDaConta ?? "Subconta").toLowerCase()}`} classe="mtc-plano-casca__copiar" />
        </div>
      )}
      {plano.passo && <p className="mtc-plano-casca__passo">{plano.passo}</p>}
      {plano.acao && (
        <LinkInterno href={plano.acao.href} className={`mtc-botao ${situacao === "bloqueado" ? "mtc-botao--perigo" : "mtc-botao--primario"} mtc-botao--pequeno mtc-plano-casca__botao`}>{plano.acao.rotulo}</LinkInterno>
      )}
    </section>
  );
}
